import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import {
  useTxs,
  useTxFacets,
  useTxFilteredFacets,
  useTxStats,
  useFeePayerStats,
  TX_PAGE_LIMIT,
} from "../api/hooks";
import { PageContainer, PageTitle, Card, Loading, Flex, Button } from "../components/ui";
import { TxTable, type TxSortKey, type SortDir } from "../components/TxTable";
import { FilterPanel } from "../components/tx-filters/FilterPanel";
import { ActiveFilterChips } from "../components/tx-filters/ActiveFilterChips";
import { ResultStatsRow } from "../components/tx-filters/ResultStatsRow";
import { SavedViewsBar } from "../components/tx-filters/SavedViewsBar";
import {
  parseTxFilters,
  applyTxFilters,
  toApiQuery,
  activeFilterCount,
  describeFilters,
  clearFilter,
  EMPTY_TX_FILTERS,
  type TxFilterState,
} from "../lib/tx-filters";
import { theme } from "../lib/theme";

const PaginationInfo = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xxl} ${theme.spacing.md};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.sm};

  button {
    margin-top: ${theme.spacing.md};
  }
`;

export function Transactions() {
  const { selectedNetwork } = useNetworkStore();
  // No local search box: looking a hash, address or label up is the global search's job, and it
  // resolves far more than an ILIKE over this one table ever did.
  //
  // The whole view — page, sort and every filter — lives in the URL, so a dialed-in slice of the
  // network is shareable and survives reloads and network switches.
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1;
  const sort = (searchParams.get("sort") ?? "blockNumber") as TxSortKey;
  const order: SortDir = searchParams.get("order") === "asc" ? "asc" : "desc";
  const filters = useMemo(() => parseTxFilters(searchParams), [searchParams]);
  const apiFilters = useMemo(() => toApiQuery(filters), [filters]);

  const { data, isLoading, isFetching } = useTxs(selectedNetwork, page, {
    ...apiFilters,
    sort,
    order,
  });
  const activeCount = activeFilterCount(filters);
  const { data: facets } = useTxFacets(selectedNetwork);
  const { data: filteredFacets } = useTxFilteredFacets(selectedNetwork, apiFilters, activeCount > 0);
  const { data: stats } = useTxStats(selectedNetwork, apiFilters);
  const { data: feePayerStats } = useFeePayerStats(selectedNetwork);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / TX_PAGE_LIMIT));
  const chips = useMemo(() => describeFilters(filters), [filters]);

  const handleFilterChange = (next: TxFilterState) => {
    setSearchParams(applyTxFilters(searchParams, next), { replace: true });
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setSearchParams(next, { replace: true });
  };

  const handleSort = (col: TxSortKey) => {
    const next = new URLSearchParams(searchParams);
    if (sort === col) {
      next.set("order", order === "desc" ? "asc" : "desc");
    } else {
      next.set("sort", col);
      next.set("order", "desc");
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  if (isLoading && !data) return <Loading />;

  return (
    <PageContainer>
      <PageTitle>Transactions</PageTitle>

      {/* `view` rides along untouched by the filter params (applyTxFilters keeps unrelated ones):
          it marks that this page was opened to edit a saved view. */}
      <SavedViewsBar
        networkId={selectedNetwork}
        filters={filters}
        editingViewId={searchParams.get("view") ?? undefined}
      />

      <FilterPanel
        facets={facets}
        filteredFacets={activeCount > 0 ? filteredFacets : undefined}
        filters={filters}
        onChange={handleFilterChange}
        feePayers={feePayerStats?.feePayers}
        summary={
          activeCount > 0
            ? `${activeCount} active${stats ? ` · ${stats.pct.toFixed(1)}% of network` : ""}`
            : undefined
        }
      />

      <ActiveFilterChips
        chips={chips}
        onRemove={(key) => handleFilterChange(clearFilter(filters, key))}
        onClearAll={() => handleFilterChange(EMPTY_TX_FILTERS)}
      />

      <ResultStatsRow stats={stats} />

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {total === 0 && !isLoading && activeCount > 0 ? (
          <EmptyState>
            No transactions match these filters.
            <br />
            <Button variant="ghost" onClick={() => handleFilterChange(EMPTY_TX_FILTERS)}>
              Clear all filters
            </Button>
          </EmptyState>
        ) : (
          <TxTable rows={data?.data ?? []} sortKey={sort} sortDir={order} onSort={handleSort} />
        )}
      </Card>

      <Flex
        justify="space-between"
        wrap
        style={{ marginTop: theme.spacing.md, alignItems: "center" }}
      >
        <PaginationInfo>
          {total.toLocaleString()} transaction{total !== 1 ? "s" : ""}
          {isFetching && " · loading…"}
        </PaginationInfo>
        <Flex gap="12px" style={{ alignItems: "center" }}>
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <PaginationInfo>
            Page {page} of {totalPages.toLocaleString()}
          </PaginationInfo>
          <Button variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </Flex>
      </Flex>
    </PageContainer>
  );
}
